import React, { ReactNode, useContext, useState } from "react";
import { FetchApi } from "../api/fetch";
import { ENDPOINT,  GO_DE_GQL_BACKEND_URL } from "../constants";
import { collectionNodes, deletedNodes, draftNodes, mintCalenderNodes, ordinalCollectionNodes, ordinalDraftNodes, ordinalUpdatedCollectionNodes } from "../constants/queries";
import {
  Collection,
  CollectionStage,
  SoloUsers,
  UserMetadata,
  UserRole,
  Launchpad,
  CollectionState,
} from "../types";
import { Blockchain } from "../pages/private/ManageDraft/types";

type ApiType = {
  collections: Collection[];
  liveCollections: Collection[];
  verifiedCollections: Collection[];
  unverifiedCollections: Collection[];
  draftCollections: Collection[];
  pendingCollections: Collection[];
  updatedCollections: Collection[];
  rejectedCollections: Collection[];
  isLoadingCollections: boolean;
  refreshCollections: (
    userJWT: string,
    userMetadata: UserMetadata
  ) => Promise<void>;
  retrieveLaunchpads: (userMetadata: UserMetadata, currentUser: any) => Promise<Launchpad[]>;
  retrieveCollections: (
    userJWT: string,
    userMetadata: UserMetadata
  ) => Promise<void>;
  retrieveDeletedCollections: (
    vars?: any,
    blockchain?:string
  ) => Promise<void>;
  retrieveDrafts: (
    userJWT: string,
    userMetadata: UserMetadata,
    params?: any
  ) => Promise<void>;
  retrieveCollectionsByType: (
    vars: any,
    type: number,
    blockchain?: string
  ) => Promise<void>;
  retrieveMintCalendar: (
    vars?: any,
  ) => Promise<void>;
  findCollection: (collectionName: string) => Collection | undefined;
  uploadFile: (
    file: File,
    progressChecker?: any
  ) => Promise<any>;
};

const ApiContext = React.createContext<ApiType>({
  collections: [],
  verifiedCollections: [],
  unverifiedCollections: [],
  liveCollections: [],
  draftCollections: [],
  pendingCollections: [],
  updatedCollections: [],
  rejectedCollections: [],
  isLoadingCollections: false,
  retrieveLaunchpads: () => {
    return Promise.resolve({} as Launchpad[]);
  },
  refreshCollections: () => {
    return Promise.resolve();
  },
  retrieveCollections: () => {
    return Promise.resolve();
  },
  retrieveDeletedCollections: () => {
    return Promise.resolve();
  },
  retrieveDrafts: () => {
    return Promise.resolve();
  },
  retrieveCollectionsByType: () => {
    return Promise.resolve();
  },
  retrieveMintCalendar: () => {
    return Promise.resolve();
  },
  findCollection: () => {
    return undefined;
  },
  uploadFile: () => {
    return Promise.resolve({} as any);
  },
});

export function useApi() {
  return useContext(ApiContext);
}

type Props = {
  children: ReactNode;
};

function ApiProvider({ children }: Props) {

  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [pendingCollections, setPendingCollections] = useState<Collection[]>([]);
  const [updatedCollections, setUpdatedCollections] = useState<Collection[]>([]);
  const [draftCollections, setDraftCollections] = useState<Collection[]>([]);
  const [verifiedCollections, setVerifiedCollections] = useState<Collection[]>([]);
  const [unverifiedCollections, setUnverifiedCollections] = useState<Collection[]>([]);
  const [liveCollections, setLiveCollections] = useState<Collection[]>([]);
  const [rejectedCollections, setRejectedCollections] = useState<Collection[]>([])


  async function retrieveCollectionsByType(vars: any, type: number, blockchain?: string) {
    // convert variable object into acceptable gql params
    let variables = ""

    for (let key in vars) {
      const val = typeof (vars[key]) === "string" ? `"${vars[key]}"` : vars[key];
      if (vars[key] === undefined || vars[key] === null) variables = `${variables}`
      else variables = `${variables}${key}:${val},`
    }
    if (blockchain == Blockchain.Bitcoin) {
      const allCollections = await FetchApi({
        endpoint: `${GO_DE_GQL_BACKEND_URL}`,
        method: "POST",
        body: {
          query: `
              {
                ${type === CollectionState.live ?
              `ordinalsCollection(${variables}){
                      ${ordinalCollectionNodes}
                    }` :
              type === CollectionState.updated ?
                `ordinalsUpdatedCollection(${variables}){
                      ${ordinalUpdatedCollectionNodes}
                    }` :
                `ordinalsDraftCollection(${variables}){
                      ${ordinalDraftNodes}
                    }`
            }
              }
            `, variables: {}
        }
      })
      return allCollections && type === CollectionState.live ? allCollections?.data?.ordinalsCollection?.nodes : type === CollectionState.updated ? allCollections?.data?.rdinalsUpdatedCollection?.nodes : allCollections?.data?.ordinalsDraftCollection?.nodes
    } else {
      const allCollections = await FetchApi({
        endpoint: `${GO_DE_GQL_BACKEND_URL}`,
        method: "POST",
        body: {
          query: `
              {
                ${type === CollectionState.live ?
              `collections(${variables}){
                      ${collectionNodes}
                    }` :
              type === CollectionState.updated ?
                `updatedCollections(${variables}){
                      ${collectionNodes}
                    }` :
                `draftCollections(${variables}){
                      ${draftNodes}
                    }`
            }
              }
            `, variables: {}
        }
      })
      return allCollections && type === CollectionState.live ? allCollections?.data?.collections?.nodes : type === CollectionState.updated ? allCollections?.data?.updatedCollections?.nodes : allCollections?.data?.draftCollections?.nodes
    }


  }

  async function retrieveMintCalendar(vars?: any) {
    setIsLoadingCollections(true);
    let variables = ""

    for (let key in vars) {
      const val = typeof (vars[key]) === "string" ? `"${vars[key]}"` : vars[key];
      if (vars[key] === undefined || vars[key] === null) variables = `${variables}`
      else variables = `${variables}${key}:${val},`
    }
    const allMintCals = await FetchApi({
      endpoint: `${GO_DE_GQL_BACKEND_URL}`,
      method: "POST",
      body: {
        query: `
            {
              mintCalendar(${variables}){
                ${mintCalenderNodes}
              }
            }
          `, variables: {}
      }
    })
    setIsLoadingCollections(true);
    return allMintCals && allMintCals?.data?.mintCalendar?.nodes
  }

  async function retrieveDeletedCollections(vars?: any,blockchain?:string) {
    setIsLoadingCollections(true);
    let variables = ""

    for (let key in vars) {
      const val = typeof (vars[key]) === "string" ? `"${vars[key]}"` : vars[key];
      if (vars[key] === undefined || vars[key] === null) variables = `${variables}`
      else variables = `${variables}${key}:${val},`
    }
    if (blockchain !== Blockchain.Bitcoin) {
      const allDeleted = await FetchApi({
        endpoint: `${GO_DE_GQL_BACKEND_URL}`,
        method: "POST",
        body: {
          query: `
              {
                deletedCollections(${variables}){
                  ${deletedNodes}
                }
              }
            `, variables: {}
        }
      })
        
    setIsLoadingCollections(false);
    return allDeleted && allDeleted?.data?.deletedCollections?.nodes
    }else{
      const allDeleted = await FetchApi({
        endpoint: `${GO_DE_GQL_BACKEND_URL}`,
        method: "POST",
        body: {
          query: `
              {
                ordinalsDeletedCollection(${variables}){
                  ${ordinalCollectionNodes}
                }
              }
            `, variables: {}
        }
      })
        
    setIsLoadingCollections(false);
    return allDeleted && allDeleted?.data?.ordinalsDeletedCollection?.nodes
    }

  }

  async function retrieveCollections(
    userJWT: string,
    userMetadata?: UserMetadata
  ) {
    setIsLoadingCollections(true);
    const stages = [];

    // for admins
    let allExistingCollections;
    let allPendingCollection;
    let allUpdatedCollection;
    let allRejectedCollection;

    // for creators
    let myDraftCollection;
    let myLiveCollection;
    let myRejectedCollection;
    let myUpdatedCollection;

    //shared
    let verifeyedCollection;
    let unVerifeyedCollection;

    stages.push(
      ...[
        CollectionStage.draft,
        CollectionStage.pending,
        CollectionStage.live,
        CollectionStage.rejected,
      ]
    );

    if (userMetadata?.role === UserRole.admin) {
      // unVerifeyedCollection = await retrieveCollectionsByType({ verifeyed: false, offset: 0, limit: 100,state: CollectionState.live }, CollectionState.live)
      myDraftCollection = await retrieveCollectionsByType({ offset: 0, limit: 10, orderBy: '-createdAt' }, 6)
      allPendingCollection = await retrieveCollectionsByType({ state: CollectionState.pending, offset: 0, limit: 10, orderBy: '-modifiedAt' }, CollectionState.live)
      allUpdatedCollection = await retrieveCollectionsByType({ state: CollectionState.updated, orderBy: '-modifiedAt', offset: 0, limit: 10 }, CollectionState.updated)
      verifeyedCollection = await retrieveCollectionsByType({ verifeyed: true, offset: 0, limit: 10, state: CollectionState.live, orderBy: '-modifiedAt' }, CollectionState.live);
      allRejectedCollection = await retrieveCollectionsByType({ state: CollectionState.rejected, offset: 0, limit: 10, orderBy: 'modifiedAt' }, CollectionState.live)
    }
    else {
      if (userMetadata?.userId) {
        myDraftCollection = await retrieveCollectionsByType({ creatorId: userMetadata?.userId, orderBy: '-createdAt', offset: 0, limit: 10 }, 6)
        // myUpdatedCollection = await retrieveCollectionsByType({ creator: userMetadata?.userId, state: CollectionState.updated }, CollectionState.updated)
        verifeyedCollection = await retrieveCollectionsByType({ creatorId: userMetadata?.userId, verifeyed: true, offset: 0, limit: 10, orderBy: '-modifiedAt' }, CollectionState.live);
        unVerifeyedCollection = await retrieveCollectionsByType({ creatorId: userMetadata?.userId, verifeyed: false, offset: 0, limit: 10, state: CollectionState.pending, orderBy: '-modifiedAt' }, CollectionState.live);
        myRejectedCollection = await retrieveCollectionsByType({ creatorId: userMetadata?.userId, state: CollectionState.rejected, offset: 0, limit: 10, orderBy: '-modifiedAt' }, CollectionState.live)

      }

      // for creators
      myDraftCollection = myDraftCollection ? myDraftCollection : [];
      myLiveCollection = myLiveCollection ? myLiveCollection : [];
      myRejectedCollection = myRejectedCollection ? myRejectedCollection : [];
      myUpdatedCollection = myUpdatedCollection ? myUpdatedCollection : [];

      // for admins
      allExistingCollections = allExistingCollections ? allExistingCollections : [];
      allPendingCollection = allPendingCollection ? allPendingCollection : [];
      allUpdatedCollection = allUpdatedCollection ? allUpdatedCollection : [];
      allRejectedCollection = allRejectedCollection ? allRejectedCollection : [];

      // for both depends on who's logged
      verifeyedCollection = verifeyedCollection ? verifeyedCollection : [];
      unVerifeyedCollection = unVerifeyedCollection ? unVerifeyedCollection : [];
    }


    if (userMetadata?.role === UserRole.admin) {
      // setCollections(allExistingCollections);
      setDraftCollections(myDraftCollection);
      setPendingCollections(allPendingCollection);
      setUpdatedCollections(allUpdatedCollection);
      setRejectedCollections(allRejectedCollection);
    } else {
      // setCollections(myLiveCollection);
      setUnverifiedCollections(unVerifeyedCollection);
      setDraftCollections(myDraftCollection);
      // setUpdatedCollections(myUpdatedCollection);
      setRejectedCollections(myRejectedCollection);
    }

    setVerifiedCollections(verifeyedCollection);
    setUnverifiedCollections(unVerifeyedCollection)

    setIsLoadingCollections(false);
  }

  async function retrieveDrafts(
    userJWT: string,
    userMetadata?: UserMetadata,
    params?: any
  ) {
    setIsLoadingCollections(true);
    // for creators
    let myDraftCollection;

    if (userMetadata?.userId) {
      myDraftCollection = await retrieveCollectionsByType({ limit: 100, ...params }, 6)
    }
    myDraftCollection = myDraftCollection ? myDraftCollection : [];
    setDraftCollections(myDraftCollection);
    setIsLoadingCollections(false);
  }

  async function retrieveLaunchpads(
    userMetadata?: UserMetadata,
    currentUser?: any,
  ) {


    const response = await FetchApi({
      endpoint: `${GO_DE_GQL_BACKEND_URL}`,
      method: "POST",
      body: {
        query: `{
                    projects${userMetadata?.role !== UserRole.admin ? `(Creator: "${currentUser}")` : ``} {
                      nodes {
                         AssetsUrl
                         BannerUrl
                         CandyMachineConfig
                         CandyMachineKey
                         CarterUrl
                         Category
                         CollectionId
                         Creator
                         Folder
                         HeroSpot
                         IsEndorsed
                         IsFeatured
                         ItemsAvailable
                         ItemsRedeemed
                         MaxWhitelists
                         Name
                         OwnerWalletKey
                         PresaleDate
                         RequestId
                         Revenue
                         ProjectState
                         ThumbnailUrl
                         Uuid
                         WalletKey
                         WhitePaperUrl
                         WhitelistTokenDistributed
                         Description
                         }
                    }
                  }
                  `,
        variables: {}
      }
    });

    return response.data.projects.nodes;
  }

  async function refreshCollections(
    userJWT: string,
    userMetadata: UserMetadata
  ) {
    return retrieveCollections(userJWT, userMetadata);
  }


  function findCollection(collectionName: string) {
    return !isLoadingCollections
      ? collections?.find((collection) => collection.name === collectionName)
      : undefined;
  }

 
  async function uploadFile(
    file: File,
    fileName?: string,
    progressChecker?: any
  ) {
    const userJWT = JSON.parse(sessionStorage.getItem('userData') || '{}').token
    console.log(file, typeof (file));

    var formData = new FormData();
    formData.append('file', file);
    formData.append('filename', fileName || file.name);
    const upload = await fetch(`${ENDPOINT.UPDATE_LIVE_COLLECTION}/upload-to-s3`, {
      method: "POST",
      headers: {
        // "Content-type": "multipart/form-data",
        Authorization: userJWT ? `Bearer ${userJWT}` : "",
      },
      body: formData
    })

    return upload
  }

  const value = {
    collections,
    verifiedCollections,
    unverifiedCollections,
    liveCollections,
    draftCollections,
    isLoadingCollections,
    pendingCollections,
    updatedCollections,
    rejectedCollections,
    refreshCollections,
    retrieveDeletedCollections,
    retrieveCollections,
    retrieveDrafts,
    retrieveMintCalendar,
    retrieveCollectionsByType,
    findCollection,
    uploadFile,
    retrieveLaunchpads,
  };

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export default ApiProvider;
